import type { CSSProperties } from "react";
import * as moment from "moment-timezone";
import "moment/locale/es";

/** Fecha legible: 05 abril 2026 */
export const formatReporteChequeoDate = (dateInput: string) =>
  moment.utc(dateInput).locale("es").format("DD MMMM YYYY");

const numericFontFamily =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace';

/** Mismo gris del thead de incidencias (TableIncidents / Incidents.tsx) */
const tableHeaderBg = "#495057";

/** Amarillo suave (filas de totales por día y acentos) */
const brandAmarillo = "#F7B917";
const brandAmarilloDark = "#e5a80f";
const daySumBgLight = "#fffbeb";
const daySumBgMid = "#fef3c7";
const daySumHoursBg = "#fde68a";
const daySumText = "#111827";
const daySumHoursText = "#111827";
const daySumBorder = "1px solid #f5d565";

/** Ancho mínimo: en pantallas chicas se desplaza horizontal en lugar de aplastar columnas. */
export const REPORTE_CHEQUEO_TABLE_MIN_WIDTH_PX = 1080;

export const REPORTE_CHEQUEO_COLUMN_COUNT = 9;

/** Columnas antes de «Horas trabajadas» (Chilito → Salida). */
export const REPORTE_CHEQUEO_DAY_SUM_LABEL_COLSPAN = 6;

/** Anchos de columna (suman 100%) — alinean thead, filas y totales por día. */
export const REPORTE_CHEQUEO_COLUMN_WIDTHS = [
  "17%",
  "15%",
  "8%",
  "12%",
  "9%",
  "9%",
  "10%",
  "13%",
  "7%",
] as const;

/** Estilos compartidos — solo UI; no altera datos ni columnas. */
export const reporteChequeoTableUi = {
  wrapper: {
    width: "100%",
    maxWidth: "100%",
    borderRadius: 0,
    border: "none",
    overflow: "hidden",
    background: "#ffffff",
    boxShadow: "none",
    margin: 0,
  } satisfies CSSProperties,

  scroll: {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  } satisfies CSSProperties,

  table: {
    width: "100%",
    minWidth: REPORTE_CHEQUEO_TABLE_MIN_WIDTH_PX,
    marginBottom: 0,
    borderCollapse: "collapse",
    borderSpacing: 0,
    fontSize: "0.8125rem",
    tableLayout: "fixed",
  } satisfies CSSProperties,

  thead: {
    background: tableHeaderBg,
    position: "sticky",
    top: 0,
    zIndex: 2,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
  } satisfies CSSProperties,

  th: {
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    padding: "12px 14px",
    borderTop: "1px solid #dee2e6",
    borderBottom: "1px solid #dee2e6",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    textAlign: "center",
  } satisfies CSSProperties,

  dataRow: {
    transition: "background-color 0.15s ease",
  } satisfies CSSProperties,

  td: {
    padding: "10px 14px",
    color: "#334155",
    verticalAlign: "middle",
    borderBottom: "1px solid #f1f5f9",
    background: "#ffffff",
  } satisfies CSSProperties,

  tdText: {
    textAlign: "left" as const,
    maxWidth: "220px",
  },

  thPartner: {
    textAlign: "center" as const,
    padding: "14px 12px",
    verticalAlign: "middle",
    fontWeight: 700,
  },

  tdPartner: {
    textAlign: "center" as const,
    verticalAlign: "middle",
    padding: "14px 12px",
    whiteSpace: "normal",
    overflowWrap: "break-word",
  },

  partnerName: {
    fontWeight: 800,
    color: "#111827",
    lineHeight: 1.35,
    overflowWrap: "break-word",
    wordBreak: "normal",
  } satisfies CSSProperties,

  thPosition: {
    textAlign: "center" as const,
    padding: "14px 12px",
    verticalAlign: "middle",
  },

  tdPosition: {
    textAlign: "center" as const,
    verticalAlign: "middle",
    padding: "14px 12px",
    fontSize: "0.75rem",
    color: "#9ca3af",
    fontWeight: 400,
    lineHeight: 1.4,
    whiteSpace: "normal",
    overflowWrap: "break-word",
    wordBreak: "normal",
  },

  tdCenter: {
    textAlign: "center" as const,
  },

  tdNumeric: {
    textAlign: "center" as const,
    fontFamily: numericFontFamily,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.02em",
  },

  tdHours: {
    textAlign: "center" as const,
    fontWeight: 600,
    color: "#b45309",
  },

  daySumRow: {
    background: `linear-gradient(90deg, ${daySumBgLight} 0%, ${daySumBgMid} 100%)`,
  } satisfies CSSProperties,

  daySumLabel: {
    textAlign: "right" as const,
    padding: "6px 14px",
    color: daySumText,
    fontWeight: 800,
    fontSize: "0.8125rem",
    background: daySumBgLight,
    borderTop: daySumBorder,
    borderBottom: daySumBorder,
    borderLeft: daySumBorder,
    borderRight: "none",
  },

  daySumHours: {
    textAlign: "center" as const,
    padding: "6px 14px",
    color: daySumHoursText,
    fontWeight: 900,
    fontSize: "0.875rem",
    background: daySumHoursBg,
    borderTop: daySumBorder,
    borderBottom: daySumBorder,
    borderLeft: daySumBorder,
    borderRight: daySumBorder,
  },

  daySumCellEmpty: {
    padding: "6px 14px",
    background: daySumBgLight,
    borderTop: daySumBorder,
    borderBottom: daySumBorder,
    borderLeft: "none",
    borderRight: "none",
  },

  daySumCellEmptyLast: {
    padding: "6px 14px",
    background: daySumBgLight,
    borderTop: daySumBorder,
    borderBottom: daySumBorder,
    borderLeft: "none",
    borderRight: daySumBorder,
  },

  grandSumRow: {
    background: tableHeaderBg,
  } satisfies CSSProperties,

  grandSumLabel: {
    textAlign: "right" as const,
    padding: "12px 14px",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.875rem",
    background: tableHeaderBg,
  },

  grandSumHours: {
    textAlign: "center" as const,
    padding: "12px 14px",
    color: "#ffffff",
    fontWeight: 800,
    fontSize: "0.9375rem",
    background: `linear-gradient(135deg, ${brandAmarillo} 0%, ${brandAmarilloDark} 100%)`,
  },

  grandSumCellEmpty: {
    padding: "12px 14px",
    background: tableHeaderBg,
  },

  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    padding: 0,
    border: "none",
    borderRadius: "10px",
    background: `linear-gradient(135deg, ${brandAmarillo} 0%, ${brandAmarilloDark} 100%)`,
    color: "#ffffff",
    boxShadow: "0 2px 8px rgba(247, 185, 23, 0.4)",
    cursor: "pointer",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  } satisfies CSSProperties,

  emptyCell: {
    padding: "48px 24px",
    background: "#fafafa",
  } satisfies CSSProperties,
};

export const reporteChequeoDataRowHover = "#f8fafc";
